import { NotePencilIcon } from '@phosphor-icons/react';
import { TableColumn, InlineEditConfig } from '@/types/table';
import { CellEditor } from './CellEditor';

interface TableCellProps<TData> {
    column: TableColumn<TData>;
    row: TData;
    rowId: string;
    style?: React.CSSProperties;
    renderContent: (value: unknown) => React.ReactNode;
    inlineEditConfig?: InlineEditConfig<TData>;
}

export function TableCell<TData>({
    column,
    row,
    rowId,
    style,
    renderContent,
    inlineEditConfig,
}: TableCellProps<TData>) {
    const columnId = column.id ?? String(column.accessor);
    const isEditable = column.editable && column.accessor && inlineEditConfig;
    const isEditing =
        inlineEditConfig?.editingCell?.rowId === rowId &&
        inlineEditConfig?.editingCell?.columnId === columnId;

    const originalValue = column.accessor ? row[column.accessor] : undefined;
    const pendingChange = inlineEditConfig?.pendingChanges.get(
        `${rowId}:${columnId}`,
    );
    const configDisplayValue = column.editConfig?.displayValue;
    const cellValue = pendingChange?.newValue ?? originalValue;
    const renderedValue = pendingChange
        ? pendingChange.newValue
        : (configDisplayValue ?? originalValue);

    const handleClick = (e: React.MouseEvent) => {
        if (isEditable && !isEditing && inlineEditConfig) {
            e.stopPropagation();
            inlineEditConfig.onEditStart({ rowId, columnId });
        }
    };

    const handleCommit = (newValue: string) => {
        if (!column.accessor || !inlineEditConfig) return;
        inlineEditConfig.onEditCommit({
            rowId,
            columnId,
            accessor: column.accessor,
            originalValue,
            newValue,
            row,
        });
    };

    if (isEditing && inlineEditConfig) {
        return (
            <td
                style={style}
                className="align-middle text-[14px] leading-5 font-normal text-white-100 p-0 bg-grey-600 border border-grey-500"
            >
                <div className="px-4 pt-3 pb-2">
                    <CellEditor
                        value={String(cellValue ?? '')}
                        onCommit={handleCommit}
                        onCancel={() => inlineEditConfig.onEditCancel()}
                        editConfig={column.editConfig}
                    />
                </div>
            </td>
        );
    }

    if (isEditable) {
        return (
            <td
                style={style}
                className="align-middle text-[14px] leading-5 font-normal text-white-100 p-0 group/cell hover:bg-surface-hover"
            >
                <div
                    onClick={handleClick}
                    className="cursor-pointer flex items-center justify-between gap-3 px-4 py-3"
                >
                    <div className="truncate min-w-0">
                        {renderContent(renderedValue)}
                    </div>
                    <NotePencilIcon
                        className="w-4 h-4 text-grey-200 opacity-0 group-hover/cell:opacity-100 transition-opacity flex-shrink-0"
                        weight="regular"
                    />
                </div>
            </td>
        );
    }

    return (
        <td
            style={style}
            className="align-middle text-[14px] leading-5 font-normal text-white-100 px-4 py-3"
        >
            <div className="truncate">{renderContent(originalValue)}</div>
        </td>
    );
}
